/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import PropTypes from 'prop-types';

const ConditionalPropTypes = {
  /**
   * The field whose value is the target of this conditional.
   */
  field: PropTypes.string.isRequired,

  /**
   * The operator for this conditional. The operator controls the method in which
   * we verify the conditional is met.
   *
   * @see `DefaultConditionalProvider#SUPPORTED_OPERATORS` for supported operators
   *
   */
  operator: PropTypes.string,

  /**
   * A comparison value that is used by certain operators when validating the conditional.
   */
  value: PropTypes.any
};

const ConditionalPropShape = PropTypes.shape(ConditionalPropTypes);

/**
 * PropTypes for a Mapping. A Mapping represents the transformation of an object
 * from one state to another.
 */
const PropertyMappingPropTypes = {
  /**
   * Set of conditionals used to check whether this mapping should apply.
   */
  when: PropTypes.arrayOf(ConditionalPropShape),

  /**
   * Whether or not to copy the value rather than move it. Defaults to false.
   */
  copy: PropTypes.bool,

  /**
   * The property to map from, or '.' to target the base.
   */
  from: PropTypes.string,

  /**
   * For collections, whether or not to merge or replace the collection.
   */
  operation: PropTypes.oneOf(['MERGE', 'REPLACE']),

  /**
   * The property to map to, or '.' to target the base.
   */
  to: PropTypes.string.isRequired,

  /**
   * If `from` is not supplied, this provided a static value to map into `to`.
   */
  value: PropTypes.any
};

const MappingPropShape = PropTypes.oneOfType([
  PropTypes.shape(PropertyMappingPropTypes),
  PropTypes.object
]);

/**
 * Prop types for a body transform. Body transforms allow us to map the request
 * and response bodies for various requests to better conform to the structure
 * our components expect.
 */
const TransformBodyPropTypes = {
  /**
   * A set of mappings to be applied to the body.
   */
  mappings: PropTypes.arrayOf(MappingPropShape),
  /**
   * A set of field names to omit in the body.
   */
  omit: PropTypes.arrayOf(PropTypes.string),

  /**
   * A set of field names to include in the body.
   */
  pick: PropTypes.arrayOf(PropTypes.string)
};

const TransformBodyPropShape = PropTypes.shape(TransformBodyPropTypes);

const EndpointPropTypes = {
  /**
   * The headers used when invoking the endpoint. Values for the headers can
   * reference context parameters using variable expressions, e.g. pass the
   * current sandbox using `${tracking.sandbox.currentSandboxId}`.
   *
   * The set of context parameters available are different between each endpoint
   * type and component type.
   */
  headers: PropTypes.object,

  /**
   * The ID for this endpoint. This property is used as a unique reference
   * for the endpoint in order to distinguish it from other similar endpoints.
   *
   * Typically, this is used in the augmentation pipeline as a unique target
   * for an augmentation.
   */
  id: PropTypes.string,

  /**
   * The optional HTTP request method for the endpoint.
   */
  method: PropTypes.string,

  /**
   * The operation type required for this endpoint. This is used for determining
   * whether or not the user has access to execute the behavior of this endpoint
   * in combination with `scope`.
   *
   * This property is optional and will be defaulted to "READ" if left undefined.
   */
  operationType: PropTypes.string,

  /**
   * The request parameters used when invoking the endpoint. Values for the
   * request parameters can reference context parameters using variable
   * expressions, e.g. target a query filter using `${filter.query}`.
   *
   * The set of context parameters available are different between each endpoint
   * type and component type.
   */
  params: PropTypes.object,

  /**
   * The scope required for this endpoint. This is used for determining whether
   * or not the user has access to execute the behavior of this endpoint in
   * combination with `operationType`.
   *
   * This property is optional as it is possible that certain resource services
   * might not require a scope.
   */
  scope: PropTypes.string,

  /**
   * Transform the request body for this endpoint's request. Useful for mapping
   * form state into a proprietary format before sending the request.
   */
  transformRequestBody: TransformBodyPropShape,

  /**
   * Transform the response body for this endpoint's request. Useful for mapping
   * nested properties into the form state.
   */
  transformResponseBody: TransformBodyPropShape,

  /**
   * The type of the endpoint. The "type" of the endpoint is used for discriminating
   * between the different endpoints a component might require. For example,
   * the Entity view component contains endpoints of type `FETCH` and type `CREATE`.
   */
  type: PropTypes.string.isRequired,

  /**
   * The URI for this endpoint. This is typically the relative url for the
   * resource service this endpoint is targeting.
   */
  uri: PropTypes.string.isRequired
};

const EndpointPropShape = PropTypes.shape(EndpointPropTypes);

const ActionPropTypes = {
  /**
   * The custom attributes for this action. This is a map of arbitrary attributes
   * that are used by the action. This is typically used for specific properties
   * that an action needs that adds a level of control for the metadata creator.
   *
   * This property is optional as many actions do not have attributes and usually
   * each action will have a different set of attributes from another.
   */
  attributes: PropTypes.shape({
    /**
     * An array of value mappings from the parent form to the item creation form.
     * These take the JSON format of `{ from, to, value }`, where `from` or
     * `value` provide the value to copy and `to` is the name of the field
     * to copy the value into. For example, `{ from: 'id', to: 'product.id' }`
     * would result in the parent form's `id` field value being copied to the
     * create form's `product.id` field.
     *
     * NOTE: This is used for external/resident grid actions
     */
    mapInheritedValues: PropTypes.arrayOf(
      PropTypes.shape({
        // the parent field whose value should be copied
        from: PropTypes.string,
        // the field on the create form to copy the value to
        to: PropTypes.string.isRequired,
        // if no `from` is provided, this can be used to default the `to` field to a static value
        value: PropTypes.string
      })
    )
  }),

  /**
   * The components for this action. This is typically used for containing
   * components that required for the behavior of the action, e.g. an action
   * with a placement of `GRID` and type of `CREATE` will use this property for
   * containing the set of Fields needed for the create modal form.
   *
   * This property is optional as many actions do not require a set of
   * components for their behavior.
   */
  components: PropTypes.arrayOf(PropTypes.object),

  /**
   * The endpoints for this action. This is used for containing any endpoints
   * that may be necessary for the behavior of this action, e.g. an action
   * with a placement of `GRID` and type of `UPDATE` will expect an endpoint
   * for where the update request should be sent.
   *
   * This property is optional as many actions do not require an endpoint.
   */
  endpoints: PropTypes.arrayOf(EndpointPropShape),

  /**
   * The ID for this action. This property is used as a unique reference
   * for the action in order to distinguish it from other similar actions.
   *
   * Typically, this is used in the augmentation pipeline as a unique target
   * for an augmentation.
   */
  id: PropTypes.string,

  /**
   * The label for this action. This is used to provide labeling for the actions
   * that require it.
   */
  label: PropTypes.node,

  /**
   * The operation type required for this action. This is used for determining
   * whether or not the user has access to execute the behavior of this action
   * in combination with `scope`.
   *
   * This property is optional as some actions might be permitted regardless
   * of the user's permissions.
   */
  operationType: PropTypes.string,

  /**
   * The order of this action within a set of actions. This is typically used
   * for sorting the actions by the metadata service and actions should be
   * pre-sorted by the time the application consumes them.
   */
  order: PropTypes.number,

  /**
   * The placement for this action. The "placement" of the action affects two
   * key aspects of the action:
   *
   * 1. The "location" of the action within the component, e.g. `GRID` indicates
   *    the action will be rendered above the grid.
   * 2. The "context" the action has available for its behavior, e.g. `ROW` indicates
   *    the action will have the context of the row it is rendered in.
   *
   * This property is optional as certain components do not require "placement"
   * as they either have a single placement for actions, or filter only on "type".
   */
  placement: PropTypes.string,

  /**
   * The scope required for this action. This is used for determining whether
   * or not the user has access to execute the behavior of this action in
   * combination with `operationType`.
   *
   * This property is optional as some actions might be permitted regardless
   * of the user's permissions.
   */
  scope: PropTypes.string,

  /**
   * The type of this action. The "type" of the action affects both the behavior
   * and display of the action.
   *
   * This is the second-level of filtering for actions after the "placement" of
   * the action. Where placement determines the context and location, type
   * determines how the action should be rendered and behave given the
   * placement, e.g. a placement of `GRID` and type of `QUERY` would result in
   * a full-text input being rendered above the grid.
   */
  type: PropTypes.string.isRequired
};

const ActionPropShape = PropTypes.shape(ActionPropTypes);

const ComponentPropTypes = {
  /**
   * The actions for the component. Actions provide a predictable, consistent
   * structure for representing complex functional attributes of the component.
   * For example, several grid components utilize GRID and ROW actions to
   * support filtering and CRUD actions.
   *
   * This property is optional as many components do not have a use of actions.
   */
  actions: PropTypes.arrayOf(ActionPropShape),

  /**
   * The custom attributes for this component. This is a map of arbitrary attributes
   * that are used by the component. This is typically used for specific properties
   * that a component needs that adds a level of control for the metadata creator.
   *
   * This property is optional as many components do not have attributes and usually
   * each component will have a different set of attributes from another.
   */
  attributes: PropTypes.object,

  /**
   * The classifier for the component. The "classifier" of the component is used
   * to determine what class of component this metadata represents. For example,
   * a component with a classifier of "Field" would be considered a Field
   * component and so would be handled and rendered in a different way than
   * other classes of components.
   */
  classifier: PropTypes.oneOf(['View', 'External', 'Field', 'Group', 'Column']),

  /**
   * The children components for the component. This property provides a method
   * for creating a component that is composed of many other components.
   *
   * For example, the external grid collection expects a set of components
   * with a classifier of Field for defining the headers of it's `ListGrid`.
   *
   * This property is optional as many components are not composed of other
   * components. However, specific components might require children components
   * in order to function properly.
   */
  components: PropTypes.arrayOf(PropTypes.object),

  /**
   * The conditionals for the component. This is simply a set of conditions
   * that are checked to determine whether or not this component should be
   * rendered. Typically this is relevant for components rendering within a
   * form such as fields, externals, or groups.
   */
  conditionals: PropTypes.arrayOf(ConditionalPropShape),

  /**
   * The endpoints for this component. This is used for containing any endpoints
   * that may be necessary for the behavior of this component, e.g. a component
   * with classifier "Field" and type of "LOOKUP" expects an endpoint configuration
   * to know where to fetch its result set.
   *
   * This property is optional as many components do not require an endpoint.
   */
  endpoints: PropTypes.arrayOf(EndpointPropShape),

  /**
   * The label for this component. This is used to provide labeling for the
   * components that require it.
   */
  label: PropTypes.node,

  /**
   * The ID for this component. This property is used as a unique reference
   * for the component in order to distinguish it from other similar components.
   *
   * Typically, this is used in the augmentation pipeline as a unique target
   * for an augmentation.
   */
  id: PropTypes.string,

  /**
   * The order of this component relative to its siblings.
   */
  order: PropTypes.number,

  /**
   * The primary scope for this component. This is used for gating user access
   * to aspects of the component.
   *
   * This property is optional as some components might be permissive regardless
   * of the user's permissions.
   */
  scope: PropTypes.string,

  /**
   * Whether or not this component supports translations, or if this component
   * is a container such as a View or External component, whether or not it
   * allows translations of its children components.
   */
  translatable: PropTypes.bool,

  /**
   * The type of this component. The "type" of the component is used as a
   * secondary selector after the "classifier". Given a class of component "Field",
   * type is then used to distinguish what type of field should be rendered.
   */
  type: PropTypes.string.isRequired
};

const ComponentPropShape = PropTypes.shape(ComponentPropTypes);

const SystemPropertyShape = PropTypes.shape({
  key: PropTypes.string,
  value: PropTypes.string
});

export { ActionPropShape, ComponentPropShape, SystemPropertyShape };
